import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { mobileNavigation, type NavigationEntry } from '@/config/navigation';
import { VIEWS } from '@/config/views';
import { useCanSee } from '@/shared/hooks/usePermission';
import { cn } from '@/shared/lib/cn';

/** Four is what fits on the narrowest phone this has to work on. */
const MAX_VISIBLE = 4;

function TabItem({ entry }: { entry: NavigationEntry }) {
  const { t } = useTranslation();
  const view = VIEWS[entry.view];
  const canSee = useCanSee(entry.view);

  if (!canSee || !view.labelKey) return null;

  return (
    <NavLink
      to={view.path}
      end
      className={({ isActive }) =>
        cn(
          'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] transition-colors',
          isActive ? 'text-brand-900 font-medium' : 'text-ink-muted active:text-ink',
        )
      }
    >
      {entry.icon}
      <span className="max-w-18 truncate">{t(view.labelKey)}</span>
    </NavLink>
  );
}

/**
 * The bottom bar on phones; hidden from `md` up, where the sidebar takes over.
 *
 * kdoshr-app's version measures which items actually rendered and spills the
 * rest into an overflow menu, because its navigation is long and per-role.
 * kdoshsop's is short, so it takes the first few and stops. Restore the
 * overflow when a real product argument for a fifth item exists — not before.
 */
export function MobileTabBar() {
  return (
    <nav className="bg-surface border-border fixed inset-x-0 bottom-0 z-30 flex border-t md:hidden">
      {mobileNavigation.slice(0, MAX_VISIBLE).map((entry) => (
        <TabItem key={entry.view} entry={entry} />
      ))}
    </nav>
  );
}
